import ContactsApi from '@api/ContactsApi'
import SearchApi from '@api/SearchApi'
import Vue from 'vue'

import { createHelpers } from 'vuex-map-fields'
const { portalContextGetter, portalContextSetter } = createHelpers({
  getterType: 'portalContextGetter',
  mutationType: 'portalContextSetter'
})

// In my ongoing mission to eliminate mixins, I will now be recording what
// method on the new composable which is replacing this maps to a method here.

const initialState = () => {
  return {
    portalContextSelected: false,
    groupId: '',
    groupCk: '',
    searchGroupCk: '',
    parentGroupCk: '',
    groupName: 'All Groups',
    subGroupId: '',
    subGroupCk: '',
    subGroupName: 'All Subgroups',
    contactsLoading: true,
    activeTab: '',
    contacts: [],
    contactsError: false,
    contactsUserMessage: '',
    searchingGroups: false,
    subGroupsByGroupSelection: [],
    internalGroupSearchResults: [],
    internalGroupSearchName: '',
    internalGroupSearchId: '',
    activePicker: 'main-context-picker',
    groupSettings: {},
    hasGroup: false,
    hasSubGroup: false,
  }
}

const mutations = {
  portalContextSetter,
  REINITIALIZE_PORTAL_CONTEXT (state) {
    let initializedState = initialState()
    Object.keys(initializedState).forEach(key => {
      Vue.set(state, key, initializedState[key])
    });
  },

  SET_PORTAL_CONTEXT_SELECTED (state, portalContextSelected) {
    state.portalContextSelected = portalContextSelected
  },
  SET_GROUP(state, group) {
    state.groupId = group.groupId
    state.groupCk = group.groupCk
    state.groupName = group.groupName
    state.subGroupsByGroupSelection = (group.subGroups || []).map(sg => {
      sg.groupCk = group.groupCk
      return sg
    })
  },
  SET_SUB_GROUP(state, subGroup) {
    state.subGroupId = subGroup.subGroupId
    state.subGroupCk = subGroup.subGroupCk
    state.subGroupName = subGroup.subGroupName
  },
  BEGIN_LOADING_CONTACTS (state) {
    state.contactsLoading = true
  },
  SET_CONTACTS (state, contacts) {
    state.contacts = contacts
    state.contactsLoading = false
    state.contactsError = false
  },

  SET_CONTACTS_USER_MESSAGE (state, contactsResponse) {
    state.contactsUserMessage = contactsResponse.userMessage
    state.contactsError = true
    state.contactsLoading = false
  },

  SET_ACTIVE_TAB (state, activeTab) {
    state.activeTab = activeTab
  },

  SET_SEARCHING_GROUPS (state, searchingGroups) {
    state.searchingGroups = searchingGroups
  },

  SET_INTERNAL_GROUP_SEARCH_RESULTS (state, groupSearchResults) {
    state.internalGroupSearchResults = groupSearchResults
  },

  SET_ACTIVE_PICKER (state, activePickerId) {
    state.activePicker = activePickerId
  },

  ADD_PARENT_GROUP_CK(state, payload) {
    state.parentGroupCk = payload
  },

  ADD_GROUP_SEARCH_GROUP_CK(state, payload) {
    state.searchGroupCk = payload
  },

  ADD_GROUP_SETTINGS(state, payload) {
    state.groupSettings = payload
  }
}

const getters = {
  hasGroup: state => state.parentGroupCk !== '',
  hasSubGroup: state => state.subGroupCk !== '',
  portalContextGetter,
  parentGroupCk:
    state => state.parentGroupCk,
  hasParentGroupCk:
    state => state.parentGroupCk,
  parentOrGroupCk:
    state => state.parentGroupCk ? state.parentGroupCk : state.groupCk,
  searchGroupCk:
    state => state.searchGroupCk,
  contacts:
    state => state.contacts,
  contactsLoading:
    state => state.contactsLoading,
  activePicker:
    state => state.activePicker,
  portalContextNames: state => {
    return {
      groupName: state.groupName,
      groupId: state.groupId,
      subGroupName: state.subGroupName,
      subGroupId: state.subGroupId
    }
  },
  portalContext: state => {
    return {
      groupCk: state.groupCk,
      subGroupCk: state.subGroupCk
    }
  },
  activeTab:
    state => state.activeTab,
  portalContextPickerOpen:
    state => state.activeTab !== '',
  portalContextSelected:
    state => state.portalContextSelected,
  requiresPortalContext:
    (state, getters, rootState, rootGetters) => {
      return !state.portalContextSelected && rootGetters.isInternalUser
    },
  groupSelected:
    state =>
      state.groupName &&
      state.groupName !== 'All Groups' &&
      state.groupCk,
  internalGroupSearchResults:
    state => (state.internalGroupSearchResults || []).map(group => {
      group.group = `${group.groupName} - ${group.groupId}`
      return group
    }),
  searchingGroups:
    state => state.searchingGroups,
  internalGroupSearchName:
    state => state.internalGroupSearchName,
  internalGroupSearchId:
    state => state.internalGroupSearchId,
  subGroupsByGroupSelection:
    state => {
      return (state.subGroupsByGroupSelection || []).map(subGroup => {
        subGroup.group = `${subGroup.groupName} - ${subGroup.groupId}`
        subGroup.subGroup = `${subGroup.subGroupName} - ${subGroup.subGroupId}`
        return subGroup
      })
    }
}

const actions = {
  CLEAR_PORTAL_CONTEXT (context) {
    context.commit('SET_PORTAL_CONTEXT_SELECTED', false)
    context.dispatch('CLEAR_SUB_GROUP')
    context.dispatch('CLEAR_GROUP')
  },

  CLEAR_GROUP (context) {
    context.commit('SET_PORTAL_CONTEXT_SELECTED', false)

    context.commit('SET_GROUP', {
      groupId: '',
      groupCk: '',
      groupName: 'All Groups'
    });
    context.commit('ADD_GROUP_SEARCH_GROUP_CK', '');
  },

  CLEAR_SUB_GROUP (context) {
    context.commit('SET_SUB_GROUP', {
      subGroupId: '',
      subGroupCk: '',
      subGroupName: 'All Subgroups'
    });
    context.commit('ADD_PARENT_GROUP_CK', '');
  },

  SELECT_GROUP (context, groupSubGroup) {
    context.dispatch('CLEAR_SUB_GROUP').then(() => {
      context.commit('SET_GROUP', groupSubGroup)
      if (groupSubGroup.subGroups && groupSubGroup.subGroups.length === 1) {
        context.commit('SET_SUB_GROUP', groupSubGroup.subGroups[0])
        context.commit('SET_ACTIVE_TAB', '')
        context.commit('SET_PORTAL_CONTEXT_SELECTED', true)
      } else { context.commit('SET_ACTIVE_TAB', 'subGroup') }
    })
  },

  SELECT_SUB_GROUP (context, groupSubGroup) {
    context.commit('SET_GROUP', groupSubGroup)
    context.commit('SET_SUB_GROUP', groupSubGroup)
    context.commit('SET_PORTAL_CONTEXT_SELECTED', true)
    context.commit('SET_ACTIVE_TAB', '')
  },

  INTERNAL_SELECT_SUB_GROUP (context, subGroup) {
    context.commit('SET_SUB_GROUP', subGroup)
    context.commit('SET_PORTAL_CONTEXT_SELECTED', true)
    context.commit('SET_ACTIVE_TAB', '')
  },

  // sets active tab on picker, and opens picker
  // maps to: openGroupSelector
  SELECT_ACTIVE_TAB (context, activeTab) {
    if (!context.state.activePicker) { context.commit('SET_ACTIVE_PICKER', 'main-context-picker') }
    return context.commit('SET_ACTIVE_TAB', activeTab)
  },

  SELECT_ACTIVE_PICKER (context, pickerId) {
    return context.commit('SET_ACTIVE_PICKER', pickerId)
  },

  SEARCH_GROUPS (context, options = {}) {
    context.commit('SET_SEARCHING_GROUPS', true)
    return new SearchApi({
      simulateExample: ''
    })
      .searchGroups({
        ...options.params
      })
      .then(results => {
        context.commit('SET_INTERNAL_GROUP_SEARCH_RESULTS', results.groups)
        context.commit('SET_SEARCHING_GROUPS', false)
      })
      .catch(_ => {
        context.commit('SET_SEARCHING_GROUPS', false)
      // todo
      })
  },

  GET_CONTACTS (context, options = {}) {
    context.commit('BEGIN_LOADING_CONTACTS')
    return new ContactsApi()
      .requestContacts({
        groupCk: context.state.groupCk,
        isInternalUser: context.rootGetters.isInternalUser
      })
      .then(contacts => {
        if (!Array.isArray(contacts)) { // there was an error or something and they returned an object
          context.commit('SET_CONTACTS_USER_MESSAGE', contacts)
        } else { // there was no error so return an array
          context.commit('SET_CONTACTS', contacts)
        }
      })
      .catch(_ => {
        // todo
      })
  },

  ADD_PARENT_GROUP_CK({ commit }, payload) {
    commit('ADD_PARENT_GROUP_CK', payload)
  },

  ADD_GROUP_SEARCH_GROUP_CK({ commit }, payload) {
    commit('ADD_GROUP_SEARCH_GROUP_CK', payload)
  },

  ADD_GROUP_SETTINGS({ commit }, payload) {
    commit('ADD_GROUP_SETTINGS', payload)
  }
}

export const portalContext = { namespaced: true, initialState, mutations, actions, getters }
